import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  ArrowDown,
  ArrowUp,
  FileText,
  MessageSquare,
  PlayCircle,
  Radio,
  Shield,
  UserRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AdminTimeRangePicker } from '@/components/AdminTimeRangePicker';
import { CountryStateFilter } from '@/components/CountryStateFilter';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { shouldShowPriorComparison } from '@/lib/timeRangeOptions';
import { cn } from '@/lib/utils';
import type {
  ModerationDashboardModuleDTO,
  ModerationReportTypeCountDTO,
} from '../dashboardTypes';
import {
  REPORTS_LOCATION_FILTER_DETAIL,
  reportsCountryLabel,
  reportsLocationCodes,
} from '../lib/reportsLocation';
import { DashboardBusy, NotReadyBadge, moderationReadyState } from './DashboardNotReady';
import { formatModCount } from './dashboardChartPrimitives';

const compact = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** Figma report-type colors: Content red, Live cyan, Creator blue, Comment green. */
const REPORT_TYPE_META: Record<string, { icon: LucideIcon; color: string }> = {
  CONTENT: { icon: FileText, color: '#f43f5e' },
  POST: { icon: FileText, color: '#f43f5e' },
  LIVE: { icon: Radio, color: '#38bdf8' },
  CREATOR: { icon: UserRound, color: '#3b82f6' },
  USER: { icon: UserRound, color: '#3b82f6' },
  COMMENT: { icon: PlayCircle, color: '#22c55e' },
  MESSAGE: { icon: MessageSquare, color: '#eab308' },
};

const FALLBACK_TYPE_COLORS = ['#f43f5e', '#38bdf8', '#3b82f6', '#22c55e', '#eab308'];

/**
 * Stable colors keyed by broad Confluence report-category codes from the dashboard API.
 */
const CATEGORY_COLOR_BY_CODE: Record<string, string> = {
  HARASSMENT: '#f43f5e',
  SPAM: '#22c55e',
  SEXUAL: '#f472b6',
  VIOLENCE: '#a78bfa',
  PRIVACY: '#38bdf8',
  HATE_SPEECH: '#b45309',
  CHILD_SAFETY: '#fb923c',
  COPYRIGHT: '#16a34a',
  IMPERSONATION: '#3b82f6',
  ILLEGAL: '#1d4ed8',
  OTHER: '#64748b',
};

const FALLBACK_CATEGORY_COLORS = ['#64748b', '#94a3b8', '#78716c'];

function categoryColor(code: string | undefined, index: number): string {
  if (code && CATEGORY_COLOR_BY_CODE[code]) return CATEGORY_COLOR_BY_CODE[code]!;
  return FALLBACK_CATEGORY_COLORS[index % FALLBACK_CATEGORY_COLORS.length]!;
}

function categoryLabel(row: ModerationReportTypeCountDTO): string {
  return row.label ?? row.type ?? 'Other';
}

function reportTypeMeta(type?: string, label?: string, index = 0) {
  const key = (type ?? label ?? '').toUpperCase().replace(/\s+/g, '_');
  const known = REPORT_TYPE_META[key];
  if (known) return known;
  for (const [k, meta] of Object.entries(REPORT_TYPE_META)) {
    if (key.includes(k) || (label ?? '').toUpperCase().includes(k)) return meta;
  }
  return {
    icon: FileText,
    color: FALLBACK_TYPE_COLORS[index % FALLBACK_TYPE_COLORS.length]!,
  };
}

/**
 * Figma Reports Overview: header filters, type donut + legend, then Report Categories bars.
 */
export function ReportsOverviewFigmaCard({
  module,
  reportTypes,
  categories,
  isLoading,
  range,
  onRangeChange,
  startDate,
  endDate,
  locationCode,
  onLocationCodeChange,
  availableCountryCodes = [],
  className,
}: {
  module?: ModerationDashboardModuleDTO | undefined;
  reportTypes?: ModerationReportTypeCountDTO[] | undefined;
  categories?: ModerationReportTypeCountDTO[] | undefined;
  isLoading?: boolean;
  range?: string;
  onRangeChange?: (range: string) => void;
  startDate?: string;
  endDate?: string;
  locationCode?: string;
  onLocationCodeChange?: (locationCode: string) => void;
  availableCountryCodes?: readonly string[];
  className?: string;
}) {
  const isDark = useIsDarkMode();
  const state = moderationReadyState(module?.availability);
  const types = reportTypes ?? [];
  const cats = categories ?? [];
  const total = types.reduce((s, t) => s + (t.count ?? 0), 0);
  const showPriorComparison = range == null || shouldShowPriorComparison(range);

  const slices = types.map((t, i) => {
    const label = t.label ?? t.type ?? 'Other';
    const { icon, color } = reportTypeMeta(t.type, t.label, i);
    const count = t.count ?? 0;
    const percentage =
      t.percentage != null
        ? Number(t.percentage)
        : total > 0
          ? (count / total) * 100
          : 0;
    return {
      key: t.type ?? label,
      label,
      count,
      percentage,
      delta: showPriorComparison ? (t.deltaPercent ?? null) : null,
      icon,
      color,
    };
  });

  const option = useMemo(() => {
    const muted = tone(CHART_INK.muted, isDark);
    const surface = tone(CHART_INK.surface, isDark);
    const visible = slices.filter((s) => s.count > 0);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        backgroundColor: surface,
        borderColor: tone(CHART_INK.grid, isDark),
        textStyle: { color: tone(CHART_INK.primary, isDark) },
        valueFormatter: (value: number | string) => Number(value).toLocaleString(),
      },
      title: [
        {
          text: 'Total',
          left: 'center',
          top: '36%',
          textStyle: { color: muted, fontSize: 11, fontWeight: 400 },
        },
        {
          text: compact.format(total),
          left: 'center',
          top: '44%',
          textStyle: { color: '#ffffff', fontSize: 26, fontWeight: 700 },
        },
        {
          text: 'Content',
          left: 'center',
          top: '60%',
          textStyle: { color: muted, fontSize: 11, fontWeight: 400 },
        },
      ],
      series: [
        {
          name: 'Report type',
          type: 'pie',
          radius: ['62%', '92%'],
          center: ['50%', '52%'],
          avoidLabelOverlap: true,
          itemStyle: { borderWidth: 0 },
          label: {
            show: true,
            position: 'inside',
            formatter: ({ data }: { data: { percentage: number } }) =>
              data.percentage >= 4 ? `${Math.round(data.percentage)}%` : '',
            color: '#ffffff',
            fontSize: 11,
            fontWeight: 700,
          },
          labelLine: { show: false },
          emphasis: { scale: true, scaleSize: 4 },
          data: visible.map((slice) => ({
            name: slice.label,
            value: slice.count,
            percentage: slice.percentage,
            itemStyle: { color: slice.color },
          })),
        },
      ],
    };
  }, [isDark, slices, total]);

  return (
    <Card className={cn('border-border/40 bg-[#1a1a1c] shadow-none', className)}>
      <CardHeader className="space-y-3 p-5 pb-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold leading-6">
            {module?.label ?? 'Reports Overview'}
          </h3>
          <NotReadyBadge state={state} {...(module?.note ? { hint: module.note } : {})} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {range != null && onRangeChange ? (
            <AdminTimeRangePicker
              preset="admin"
              value={range}
              onChange={onRangeChange}
              {...(startDate ? { startDate } : {})}
              {...(endDate ? { endDate } : {})}
              ariaLabel="Change reports overview date range"
              className="w-full min-w-[180px] sm:w-[220px]"
            />
          ) : null}
          {onLocationCodeChange != null ? (
            <CountryStateFilter
              value={locationCode ?? ''}
              onChange={onLocationCodeChange}
              availableCountryCodes={reportsLocationCodes(availableCountryCodes)}
              countryLabel={reportsCountryLabel}
              allCountriesLabel="All locations"
              detail={REPORTS_LOCATION_FILTER_DETAIL}
              ariaLabel="Change reports overview location filter"
              className="w-full min-w-[180px] flex-1 sm:flex-none sm:w-[220px]"
              title={REPORTS_LOCATION_FILTER_DETAIL}
            />
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-6 p-5 pt-0">
        {isLoading ? (
          <DashboardBusy className="h-56" />
        ) : state === 'not-ready' ? (
          <p className="text-sm text-muted-foreground">{module?.note ?? 'Not available yet.'}</p>
        ) : (
          <>
            <div className="grid items-center gap-6 lg:grid-cols-[minmax(190px,0.85fr)_minmax(240px,1.15fr)]">
              {total === 0 ? (
                <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
                  No reports in this window.
                </div>
              ) : (
                <ReactECharts option={option} notMerge style={{ height: 240, width: '100%' }} />
              )}

              <ul className="space-y-3">
                {slices.length === 0 ? (
                  <li className="text-sm text-muted-foreground">No report types for this window.</li>
                ) : (
                  slices.map((slice) => {
                    const Icon = slice.icon;
                    const rising = (slice.delta ?? 0) >= 0;
                    const DeltaIcon = rising ? ArrowUp : ArrowDown;
                    return (
                      <li key={slice.key}>
                        <div className="flex items-center gap-3">
                          <span
                            className="flex size-8 shrink-0 items-center justify-center rounded-full"
                            style={{ backgroundColor: `${slice.color}1f` }}
                          >
                            <Icon className="size-4" style={{ color: slice.color }} aria-hidden />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-baseline justify-between gap-3">
                              <span className="truncate text-sm font-semibold">{slice.label}</span>
                              <span className="shrink-0 text-sm font-bold tabular-nums">
                                {formatModCount(slice.count)}
                              </span>
                            </span>
                            <span className="mt-1 block h-1 overflow-hidden rounded-full bg-white/10">
                              <span
                                className="block h-full rounded-full"
                                style={{
                                  backgroundColor: slice.color,
                                  width: `${Math.min(Math.max(slice.percentage, 0), 100)}%`,
                                }}
                              />
                            </span>
                            <span className="mt-1 flex items-center justify-between gap-3 text-[11px]">
                              <span className="tabular-nums text-muted-foreground">
                                {Math.round(slice.percentage)}%
                              </span>
                              {slice.delta == null ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                <span
                                  className={cn(
                                    'inline-flex items-center gap-0.5 font-semibold tabular-nums',
                                    rising ? 'text-emerald-400' : 'text-rose-400',
                                  )}
                                >
                                  <DeltaIcon className="size-3" aria-hidden />
                                  {Math.abs(slice.delta).toFixed(1)}%
                                </span>
                              )}
                            </span>
                          </span>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>

            <div className="space-y-4 border-t border-border/40 pt-5">
              <div className="flex items-center gap-2.5">
                <span className="flex size-9 items-center justify-center rounded-lg bg-violet-500/20 text-violet-300">
                  <Shield className="size-4" aria-hidden />
                </span>
                <div>
                  <p className="text-base font-semibold leading-tight">Report Categories</p>
                  <p className="text-xs text-muted-foreground">By broad report reason</p>
                </div>
              </div>

              {cats.length === 0 ? (
                <p className="text-sm text-muted-foreground">No category breakdown for this window.</p>
              ) : (
                <ul className="space-y-3.5">
                  {cats.map((row, i) => {
                    const code = row.type ?? '';
                    const pct = Number(row.percentage ?? 0);
                    const color = categoryColor(code, i);
                    return (
                      <li key={code || row.label || i}>
                        <div className="mb-1.5 flex items-baseline justify-between gap-3">
                          <span className="min-w-0 truncate text-sm text-foreground">
                            {categoryLabel(row)}
                          </span>
                          <span className="flex shrink-0 items-baseline gap-3 tabular-nums">
                            <span className="font-semibold">{pct.toFixed(1)}%</span>
                            <span className="w-12 text-right text-muted-foreground">
                              {compact.format(row.count ?? 0)}
                            </span>
                          </span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.min(100, Math.max(0, pct))}%`,
                              backgroundColor: color,
                            }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
